import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
//
import useLocales from './useLocales';
import palette from '../theme/palette';
import typography from '../theme/typography';
import shadows from '../theme/shadows';
import customShadows from '../theme/customShadows';
import componentsOverride from '../theme/overrides';

// ----------------------------------------------------------------------

const langShape = PropTypes.shape({
  labelKey: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
});

export function LanguageOptionContent({ lang }) {
  const { t } = useTranslation();

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        component="img"
        src={lang.icon}
        alt=""
        sx={{
          width: 22,
          height: 15,
          display: 'block',
          borderRadius: 0.5,
          objectFit: 'cover',
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t(lang.labelKey)}
      </Typography>
    </Box>
  );
}

LanguageOptionContent.propTypes = {
  lang: langShape.isRequired,
};

// ----------------------------------------------------------------------

export default function LanguageSelect({
  size = 'small',
  fullWidth = false,
  label,
  className,
  theme: themeMode = 'auto',
  SelectProps: externalSelectProps,
  ...textFieldProps
}) {
  const outerTheme = useTheme();
  const { currentLang, allLangs, onChangeLang } = useLocales();

  const overrideTheme = useMemo(() => {
    if (themeMode === 'auto') {
      return null;
    }
    // Só trocar palette.mode não basta: o app guarda cores explícitas do dark em storage.
    // Recriamos o mesmo pipeline de src/theme/index.js com light/dark forçado.
    const forcedMode = themeMode === 'light' ? 'light' : 'dark';
    const themeOptions = {
      palette: palette(forcedMode),
      typography,
      shape: { borderRadius: 8 },
      direction: outerTheme.direction,
      shadows: shadows(forcedMode),
      customShadows: customShadows(forcedMode),
    };
    const nextTheme = createTheme(themeOptions);
    nextTheme.components = componentsOverride(nextTheme);
    return nextTheme;
  }, [outerTheme.direction, themeMode]);

  const content = (
    <Box className={className}>
      <TextField
        select
        fullWidth={fullWidth}
        label={label}
        size={size}
        value={currentLang.value}
        onChange={(event) => onChangeLang(event.target.value)}
        SelectProps={{
          ...externalSelectProps,
          renderValue: (value) => {
            const lang = allLangs.find((item) => item.value === value) ?? currentLang;
            return <LanguageOptionContent lang={lang} />;
          },
          MenuProps: {
            ...externalSelectProps?.MenuProps,
            PaperProps: {
              ...externalSelectProps?.MenuProps?.PaperProps,
              sx: {
                px: 1,
                maxHeight: 280,
                '& .MuiMenuItem-root': {
                  px: 1,
                  borderRadius: 0.75,
                  typography: 'body2',
                },
                ...externalSelectProps?.MenuProps?.PaperProps?.sx,
              },
            },
          },
        }}
        {...textFieldProps}
      >
        {allLangs.map((lang) => (
          <MenuItem key={lang.value} value={lang.value}>
            <LanguageOptionContent lang={lang} />
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  if (overrideTheme) {
    return <ThemeProvider theme={overrideTheme}>{content}</ThemeProvider>;
  }

  return content;
}

LanguageSelect.propTypes = {
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string,
  theme: PropTypes.oneOf(['auto', 'light', 'dark']),
  SelectProps: PropTypes.object,
};
